import { DefaultModuleDto } from "../../dtos/DefaultModuleDto";
import { DefaultPhaseDto } from "../../dtos/DefaultPhaseDto";
import { DefaultStepDto } from "../../dtos/DefaultStepDto";

import { PhaseWorkflowType } from "../../dtos/PhaseWorkflowType";
import { WorkflowConfigOptionsDto } from "../../dtos/WorkflowConfigOptionsDto";
import { WorkflowTypeSummaryDto } from "../../dtos/WorkflowTypeSummaryDto";
import { EmailTemplateContentDto } from "../../dtos/EmailTemplateContentDto";
import { EmailTemplateEditDto } from "../../dtos/EmailTemplateEditDto";
import { EmailTemplateSummaryDto } from "../../dtos/EmailTemplateSummaryDto";
import { ResetEmailTemplateDto } from "../../dtos/ResetEmailTemplateDto";
import { ApiResult, apiWrapperWithError, apiUploadWithError } from "./ApiUtils";

export const getDefaultModules = async (tasklistOnly?: boolean): Promise<ApiResult<DefaultModuleDto[]> | null> => {
    const querystring = tasklistOnly ? '?tasklistOnly=true' : '';
    return apiWrapperWithError<DefaultModuleDto[]>(`api/tasklists/defaults/modules${querystring}`, 'GET', `fetching default modules`);
}

export const getDefaultModule = async (id: number, grantId?: string, tasklistOnly?: boolean): Promise<ApiResult<DefaultModuleDto> | null> => {
    const params = new URLSearchParams();
    if (grantId) params.set('grantId', grantId);
    if (tasklistOnly) params.set('tasklistOnly', 'true');
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return apiWrapperWithError<DefaultModuleDto>(`api/tasklists/defaults/modules/${id}${queryString}`, 'GET', `fetching default module with id ${id}`);
}

export const getTriggerTargets = async (): Promise<ApiResult<DefaultModuleDto[]> | null> => {
    return apiWrapperWithError<DefaultModuleDto[]>(`api/tasklists/defaults/trigger-targets`, 'GET', `fetching trigger targets`);
}

export const createDefaultTask = async (id: number, data: DefaultPhaseDto): Promise<ApiResult<DefaultPhaseDto> | null> => {
    return apiWrapperWithError<DefaultPhaseDto>(`api/tasklists/defaults/modules/${id}/tasks`, 'POST', `saving default task with id ${id}`, data);
}

export const createDefaultSubtask = async (id: number, data: DefaultStepDto): Promise<ApiResult<DefaultStepDto> | null> => {
    return apiWrapperWithError<DefaultStepDto>(`api/tasklists/defaults/modules/${id}/tasks/${data.phaseTypeId}/subtasks`, 'POST', `creating default subtask for default task with id ${data.phaseTypeId}`, data);
}

export const updateDefaultSubtask = async (id: number, data: DefaultStepDto): Promise<ApiResult<DefaultStepDto> | null> => {
    return apiWrapperWithError<DefaultStepDto>(`api/tasklists/defaults/modules/${id}/tasks/${data.phaseTypeId}/subtasks/${data.id}`, 'PUT', `updating default subtask with id ${data.id}`, data);
}

export const deleteDefaultTask = async (moduleId: number, taskId: number): Promise<ApiResult<boolean> | null> => {
    return apiWrapperWithError<boolean>(`api/tasklists/defaults/modules/${moduleId}/tasks/${taskId}`, 'DELETE', `deleting default task with id ${taskId}`);
}

export const deleteDefaultSubtask = async (moduleId: number, taskId: number, subtaskId: number): Promise<ApiResult<boolean> | null> => {
    return apiWrapperWithError<boolean>(`api/tasklists/defaults/modules/${moduleId}/tasks/${taskId}/subtasks/${subtaskId}`, 'DELETE', `deleting default subtask with id ${subtaskId}`);
}

export const getWorkflowTypes = async (): Promise<ApiResult<WorkflowTypeSummaryDto[]> | null> => {
    return apiWrapperWithError<WorkflowTypeSummaryDto[]>(`api/tasklists/workflow/types`, 'GET', `fetching workflow types`);
}

// uploadOnlySubtasks is the parent task's flag: its subtasks hold In Progress only, so the
// status options narrow to that.
export const getWorkflowConfigOptions = async (workflowType: PhaseWorkflowType, defaultPhaseId?: number, uploadOnlySubtasks?: boolean, defaultStepId?: number): Promise<ApiResult<WorkflowConfigOptionsDto> | null> => {
    const params = new URLSearchParams();
    if (defaultPhaseId) params.set('defaultPhaseId', String(defaultPhaseId));
    if (uploadOnlySubtasks) params.set('uploadOnlySubtasks', 'true');
    if (defaultStepId) params.set('defaultStepId', String(defaultStepId));
    const queryString = params.toString() ? `?${params}` : '';
    return apiWrapperWithError<WorkflowConfigOptionsDto>(`api/tasklists/workflow/config/${workflowType}${queryString}`, 'GET', `fetching workflow config options for type ${workflowType}`);
}

export const getAllEmailTemplates = async (): Promise<ApiResult<EmailTemplateSummaryDto[]> | null> => {
    return apiWrapperWithError<EmailTemplateSummaryDto[]>(`api/tasklists/email-templates`, 'GET', `fetching all email templates`);
}

export type TemplateTokenInfo = { token: string; scope: string; description: string };

export const getEmailTemplateTokens = async (templateType: string): Promise<ApiResult<TemplateTokenInfo[]> | null> => {
    return apiWrapperWithError<TemplateTokenInfo[]>(`api/tasklists/email-templates/tokens/${encodeURIComponent(templateType)}`, 'GET', `fetching template tokens for ${templateType}`);
}

export const getEmailTemplate = async (templateName: string): Promise<ApiResult<EmailTemplateContentDto> | null> => {
    return apiWrapperWithError<EmailTemplateContentDto>(`api/tasklists/email-templates/${encodeURIComponent(templateName)}`, 'GET', `fetching email template ${templateName}`);
}

export const customizeEmailTemplate = async (dto: EmailTemplateEditDto): Promise<ApiResult<EmailTemplateContentDto> | null> => {
    return apiWrapperWithError<EmailTemplateContentDto>(`api/tasklists/email-templates`, 'PUT', `customizing email template ${dto.coreTemplateName}`, dto);
}

export const resetEmailTemplate = async (dto: ResetEmailTemplateDto): Promise<ApiResult<{ revertToTemplateName: string | null }> | null> => {
    return apiWrapperWithError<{ revertToTemplateName: string | null }>(`api/tasklists/email-templates/reset`, 'POST', `resetting email template ${dto.templateName}`, dto);
}

export const getTemplateAttachments = async (phaseTypeId: number): Promise<ApiResult<string[]>> => {
    return apiWrapperWithError<string[]>(`api/tasklists/template-attachments/${phaseTypeId}`, 'GET', `fetching template attachments for phase ${phaseTypeId}`);
}

export const uploadTemplateAttachment = async (phaseTypeId: number, file: File): Promise<ApiResult<{ filename: string }>> => {
    const formData = new FormData();
    formData.append('file', file);
    return apiUploadWithError<{ filename: string }>(`api/tasklists/template-attachments/${phaseTypeId}`, formData, `uploading template attachment for phase ${phaseTypeId}`);
}
